/**
 * Painel "Pedido de Nova Organização" do chamado (spec 2026-10-27 §4.6, Onda 1).
 *
 * O pedido decide pela rota dele, nunca pelo `PATCH` de status genérico (que recusa e nomeia a
 * porta). Nesta onda a única decisão é "Recusar", com motivo obrigatório; quem vê o botão é quem
 * o servidor diz que pode decidir (`pode_decidir`), e só com o pedido `pendente`. A aprovação vem
 * na Onda 2.
 *
 * 404 = o chamado não tem pedido, ou quem lê não pode: o painel não aparece.
 */
import { useState, type ReactNode } from 'react'
import { Alert, Badge, Button, Group, Modal, Paper, SimpleGrid, Stack, Text, Textarea } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ROTULO_DO_ESTADO, ROTULO_DO_TIPO, chaveDoPedidoDoChamado, lerPedidoDoChamado, motivoDaRecusa, recusarPedido,
} from '@/api/pedidosDeOrganizacao'
import { invalidarPendenciasDoChamado } from '@/api/chamadosPendencias'
import { formatarData } from './leitura/RemetenteDoChamado'

const COR_DO_ESTADO: Record<string, string> = {
  pendente: 'yellow', recusado: 'red', cancelado: 'gray', concluido: 'teal', falhou: 'red',
}

function Campo({ rotulo, children }: { rotulo: string; children: ReactNode }) {
  return (
    <Stack gap={0}>
      <Text size="xs" c="dimmed">{rotulo}</Text>
      <Text size="sm">{children}</Text>
    </Stack>
  )
}

export function PainelPedidoDeOrganizacao({ taskRequestId }: { taskRequestId: number }) {
  const qc = useQueryClient()
  const chave = chaveDoPedidoDoChamado(taskRequestId)
  const { data } = useQuery({
    queryKey: chave,
    queryFn: () => lerPedidoDoChamado(taskRequestId),
    retry: false,
  })
  const [recusando, setRecusando] = useState(false)
  const [motivo, setMotivo] = useState('')
  const [erro, setErro] = useState<string | null>(null)

  const recusar = useMutation({
    mutationFn: () => recusarPedido(taskRequestId, motivo.trim()),
    onSuccess: () => {
      setRecusando(false)
      setMotivo('')
      setErro(null)
      notifications.show({ color: 'teal', title: 'Pedido recusado', message: 'O chamado foi para recusado.' })
      void qc.invalidateQueries({ queryKey: chave })
      // A mesma chave que o ChamadoDetalhe usa (`useParams` dá o id como texto).
      void qc.invalidateQueries({ queryKey: ['chamado', String(taskRequestId)] })
      invalidarPendenciasDoChamado(qc)
    },
    onError: (err) => setErro(motivoDaRecusa(err, 'Não foi possível recusar o pedido.')),
  })

  if (!data) return null
  const { pedido } = data
  const podeRecusar = data.pode_decidir && pedido.estado === 'pendente'
  const projeto = pedido.project_slug
    ? pedido.project_name && pedido.project_name !== pedido.project_slug
      ? `${pedido.project_name} (${pedido.project_slug})`
      : pedido.project_slug
    : '—'

  return (
    <Paper withBorder p="md" radius="md">
      <Stack gap="sm">
        <Group justify="space-between">
          <Group gap="xs">
            <Text fw={600}>Pedido de Nova Organização</Text>
            <Badge color={COR_DO_ESTADO[pedido.estado] ?? 'blue'} variant="light">
              {ROTULO_DO_ESTADO[pedido.estado] ?? pedido.estado}
            </Badge>
          </Group>
          {podeRecusar && (
            <Button size="xs" color="red" variant="light" onClick={() => setRecusando(true)}>Recusar</Button>
          )}
        </Group>
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xs">
          <Campo rotulo="Organização">{pedido.nome}</Campo>
          <Campo rotulo="Tipo">{ROTULO_DO_TIPO[pedido.tipo] ?? pedido.tipo}</Campo>
          <Campo rotulo="Projeto">{projeto}</Campo>
          <Campo rotulo="Responsável">{pedido.responsavel.nome}</Campo>
          <Campo rotulo="Solicitante">{pedido.solicitante.nome}</Campo>
          <Campo rotulo="Pedido em">{formatarData(pedido.criado_em)}</Campo>
        </SimpleGrid>
        <Campo rotulo="Justificativa">{pedido.justificativa}</Campo>
        {pedido.decidido_por && (
          <Alert color={pedido.estado === 'recusado' ? 'red' : 'blue'} title="Decisão">
            <Text size="sm">
              {pedido.decidido_por.nome}
              {pedido.decidido_em ? `, em ${formatarData(pedido.decidido_em)}` : ''}
            </Text>
            {pedido.motivo_decisao && <Text size="sm">{pedido.motivo_decisao}</Text>}
          </Alert>
        )}
      </Stack>

      <Modal opened={recusando} onClose={() => setRecusando(false)} title="Recusar o pedido">
        <Stack gap="sm">
          <Text size="sm">
            O chamado vai para recusado e o slug do projeto fica livre para um pedido novo. O motivo vai para
            quem pediu.
          </Text>
          <Textarea
            label="Motivo da recusa"
            value={motivo}
            onChange={(e) => setMotivo(e.currentTarget.value)}
            required
            autosize
            minRows={3}
          />
          {erro && <Alert color="red">{erro}</Alert>}
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setRecusando(false)}>Voltar</Button>
            <Button
              color="red"
              onClick={() => recusar.mutate()}
              loading={recusar.isPending}
              disabled={!motivo.trim()}
            >
              Recusar pedido
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Paper>
  )
}
